import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, ShoppingCart, Trash2, ArrowRight, Eye } from 'lucide-react';
import { useFavorites } from '../contexts/FavoritesContext';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import LoginForm from '../components/auth/LoginForm';
import SignupForm from '../components/auth/SignupForm';
import { toast } from 'sonner';
import { ADD_TO_CART_TOAST_DURATION } from '../lib/toastConfig';
import AuthCard from '../components/layout/AuthCard';
import AuthModalClose from '../components/auth/AuthModalClose';
import { products } from '../data/productData';
import ProductPrice from '../components/product/ProductPrice';
import { getProductDisplayName } from '../utils/productDisplay';
import { getProductPath } from '../utils/productLinks';
import { webpVariant } from '../lib/optimizedImage';

const resolveImageUrl = (imageUrl) => {
  if (!imageUrl) return '/placeholder-image.jpg';
  if (imageUrl.startsWith('http') || imageUrl.startsWith('/')) return imageUrl;
  return `/img/${imageUrl}`;
};

const priceClassName =
  'flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5 [&_.line-through]:!text-slate-500 [&_span.font-bold]:!text-amber-400 [&_span.rounded]:!shrink-0 [&_span.rounded]:!bg-amber-400/10 [&_span.rounded]:!text-amber-300 [&_span.rounded]:!text-[9px] [&_span.rounded]:!px-1.5 [&_span.rounded]:!py-0.5 sm:[&_span.rounded]:!text-[10px]';

const WishlistSkeleton = () => (
  <div className="mx-auto max-w-3xl space-y-3 sm:space-y-4" aria-hidden="true">
    {Array.from({ length: 4 }).map((_, index) => (
      <div
        key={index}
        className="animate-pulse rounded-xl border border-white/5 bg-white/[0.03] p-3 sm:p-4"
      >
        <div className="flex gap-3 sm:gap-4">
          <div className="h-[72px] w-[72px] shrink-0 rounded-lg bg-slate-800 sm:h-24 sm:w-24" />
          <div className="flex min-w-0 flex-1 flex-col justify-center gap-2">
            <div className="h-3 w-1/4 rounded bg-slate-800/70" />
            <div className="h-4 w-3/4 rounded bg-slate-800" />
            <div className="h-3 w-1/3 rounded bg-slate-800/80" />
          </div>
          <div className="hidden shrink-0 flex-col justify-center gap-2 sm:flex">
            <div className="h-10 w-28 rounded-lg bg-slate-800/70" />
            <div className="h-10 w-28 rounded-lg bg-slate-800/70" />
          </div>
        </div>
        <div className="mt-3 space-y-2 sm:hidden">
          <div className="h-11 w-full rounded-lg bg-slate-800/70" />
          <div className="flex gap-2">
            <div className="h-11 flex-1 rounded-lg bg-slate-800/70" />
            <div className="h-11 w-11 rounded-lg bg-slate-800/70" />
          </div>
        </div>
      </div>
    ))}
  </div>
);

const WishlistPage = () => {
  const { favorites, removeFromFavorites, loading } = useFavorites();
  const { addToCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [showAuthModal, setShowAuthModal] = useState(false);
  const [isLogin, setIsLogin] = useState(true);

  const productById = useMemo(
    () => new Map(products.map((product) => [product.id, product])),
    []
  );

  const resolveProduct = (favorite) => {
    const catalog = productById.get(Number(favorite.product_id || favorite.id));
    if (catalog) return catalog;

    return {
      id: favorite.product_id || favorite.id,
      name: favorite.product_name || 'Unknown Product',
      image: favorite.product_image,
      price: favorite.product_price ?? 0,
      category: favorite.product_category,
    };
  };

  const handleAddToCart = (product) => {
    if (!user) {
      setShowAuthModal(true);
      setIsLogin(true);
      toast.info('Please sign in to add items to cart');
      return;
    }
    addToCart(resolveProduct(product));
    toast.success('Added to cart', {
      duration: ADD_TO_CART_TOAST_DURATION,
      style: { background: '#0f172a', color: '#fbbf24', border: '1px solid #334155' },
    });
  };

  const handleRemoveFavorite = async (productId) => {
    await removeFromFavorites(productId);
  };

  const handleViewDetails = (productId) => {
    const product = products.find((item) => item.id === productId);
    navigate(product ? getProductPath(product) : `/product/${productId}`);
  };

  const hasFavorites = favorites?.length > 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-gray-900 to-black">
        <div className="container mx-auto px-4 py-8 sm:px-6 sm:py-12">
          <div className="mb-8 text-center sm:mb-10">
            <p className="text-[11px] font-light uppercase tracking-[0.25em] text-amber-400/80 sm:text-xs sm:tracking-[0.2em]">
              Wishlist
            </p>
            <h1 className="mt-2.5 text-2xl font-light tracking-tight text-white sm:mt-2 sm:text-3xl">
              Your Collection
            </h1>
            <div className="mx-auto mt-4 h-px w-20 bg-gradient-to-r from-transparent via-amber-400/50 to-transparent sm:mt-3 sm:w-16" />
          </div>
          <WishlistSkeleton />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-gray-900 to-black">
      <div className="container mx-auto px-4 py-8 sm:px-6 sm:py-12">
        {/* Header */}
        <div className="mb-8 text-center sm:mb-10">
          <p className="text-[11px] font-light uppercase tracking-[0.25em] text-amber-400/80 sm:text-xs sm:tracking-[0.2em]">
            Wishlist
          </p>
          <h1 className="mt-2.5 text-2xl font-light tracking-tight text-white sm:mt-2 sm:text-3xl">
            Your Collection
          </h1>
          {hasFavorites && (
            <p className="mt-2 text-sm font-light tracking-wide text-slate-400">
              {favorites.length} {favorites.length === 1 ? 'item' : 'items'} saved
            </p>
          )}
          <div className="mx-auto mt-4 h-px w-20 bg-gradient-to-r from-transparent via-amber-400/50 to-transparent sm:mt-3 sm:w-16" />
        </div>

        {!user && hasFavorites && (
          <div className="mx-auto mb-6 max-w-3xl rounded-lg border border-amber-400/20 bg-amber-400/5 px-4 py-3 text-center text-sm text-amber-200/90">
            Saved on this device.{' '}
            <button
              type="button"
              onClick={() => {
                setShowAuthModal(true);
                setIsLogin(true);
              }}
              className="font-medium text-amber-300 underline-offset-2 hover:text-amber-200 hover:underline"
            >
              Sign in
            </button>{' '}
            to sync across devices.
          </div>
        )}

        {!hasFavorites ? (
          <div className="mx-auto max-w-md text-center">
            <div className="rounded-2xl border border-white/10 bg-white/[0.04] px-6 py-12 sm:px-10 sm:py-14">
              <div className="relative mx-auto mb-6 h-16 w-16">
                <div className="absolute inset-0 rounded-full bg-amber-400/10 blur-xl" />
                <div className="relative flex h-full w-full items-center justify-center rounded-full border border-white/10 bg-slate-800/50">
                  <Heart className="h-7 w-7 text-slate-500" strokeWidth={1.5} />
                </div>
              </div>

              <h2 className="text-xl font-light text-white sm:text-2xl">
                Your wishlist is empty
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-slate-400">
                Save pieces you love and find them here anytime.
              </p>

              <button
                type="button"
                onClick={() => navigate('/shop')}
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-amber-400 px-6 py-2.5 text-sm font-semibold text-slate-950 transition-colors hover:bg-amber-300"
              >
                Browse the shop
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </button>

              {!user && (
                <p className="mt-6 text-xs text-slate-500">
                  No account needed to save items on this device.{' '}
                  <button
                    type="button"
                    onClick={() => {
                      setShowAuthModal(true);
                      setIsLogin(true);
                    }}
                    className="text-amber-400/80 hover:text-amber-300"
                  >
                    Sign in
                  </button>{' '}
                  to sync later.
                </p>
              )}
            </div>
          </div>
        ) : (
          <ul className="mx-auto max-w-3xl space-y-3 sm:space-y-4">
            {favorites.map((favorite, index) => {
              const product = resolveProduct(favorite);
              const productId = product.id || favorite.product_id;
              const displayName = getProductDisplayName(product);
              const imageSrc = resolveImageUrl(product.image || favorite.product_image);
              const thumbWebp = webpVariant(imageSrc, 480);

              return (
                <li
                  key={productId || index}
                  className="group rounded-xl border border-white/10 bg-white/[0.03] transition-colors hover:border-amber-400/20 hover:bg-white/[0.05]"
                >
                  <div className="p-3 sm:p-4">
                    <div className="flex gap-3 sm:gap-4">
                      <button
                        type="button"
                        onClick={() => handleViewDetails(productId)}
                        className="relative h-[72px] w-[72px] shrink-0 overflow-hidden rounded-lg border border-white/10 bg-slate-800 sm:h-24 sm:w-24"
                        aria-label={`View ${displayName}`}
                      >
                        <picture className="block h-full w-full">
                          {thumbWebp && (
                            <source type="image/webp" srcSet={thumbWebp} />
                          )}
                          <img
                            src={imageSrc}
                            alt={displayName}
                            loading="lazy"
                            decoding="async"
                            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                            onError={(e) => {
                              e.currentTarget.src = '/placeholder-image.jpg';
                            }}
                          />
                        </picture>
                      </button>

                      <div className="flex min-w-0 flex-1 flex-col justify-center gap-1 sm:gap-1.5">
                        {product.category && (
                          <p className="text-[10px] uppercase tracking-wider text-amber-400/60 sm:text-xs">
                            {product.category}
                          </p>
                        )}
                        <button
                          type="button"
                          onClick={() => handleViewDetails(productId)}
                          className="line-clamp-2 text-left text-sm font-medium leading-snug text-white transition-colors hover:text-amber-200 sm:text-base"
                        >
                          {displayName}
                        </button>
                        <div className="mt-0.5 min-w-0 max-w-full overflow-hidden">
                          <ProductPrice
                            product={product}
                            size="xs"
                            className={`${priceClassName} sm:hidden [&_span.font-bold]:!text-sm`}
                          />
                          <ProductPrice
                            product={product}
                            size="sm"
                            className={`${priceClassName} hidden sm:inline-flex [&_span.font-bold]:!text-lg`}
                          />
                        </div>
                      </div>

                      {/* Desktop actions */}
                      <div className="hidden shrink-0 flex-col justify-center gap-2 sm:flex">
                        <button
                          type="button"
                          onClick={() => handleAddToCart(favorite)}
                          className="inline-flex min-h-[44px] items-center justify-center gap-1.5 rounded-lg bg-amber-400 px-4 py-2.5 text-xs font-semibold text-slate-950 transition-colors hover:bg-amber-300"
                        >
                          <ShoppingCart className="h-4 w-4" aria-hidden="true" />
                          Add to cart
                        </button>
                        <button
                          type="button"
                          onClick={() => handleViewDetails(productId)}
                          className="inline-flex min-h-[44px] items-center justify-center rounded-lg border border-white/15 px-4 py-2.5 text-xs text-slate-300 transition-colors hover:border-amber-400/30 hover:text-amber-200"
                        >
                          View product
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRemoveFavorite(productId)}
                          className="inline-flex min-h-[44px] items-center justify-center gap-1.5 rounded-lg border border-white/10 px-4 py-2.5 text-xs text-slate-400 transition-colors hover:border-red-400/30 hover:text-red-400"
                        >
                          <Trash2 className="h-4 w-4" aria-hidden="true" />
                          Remove
                        </button>
                      </div>
                    </div>

                    {/* Mobile actions — stacked below product row */}
                    <div className="mt-3 flex flex-col gap-2 sm:hidden">
                      <button
                        type="button"
                        onClick={() => handleAddToCart(favorite)}
                        className="inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-lg bg-amber-400 px-4 py-2.5 text-sm font-semibold text-slate-950 transition-colors hover:bg-amber-300 active:scale-[0.98]"
                      >
                        <ShoppingCart className="h-4 w-4" aria-hidden="true" />
                        Add to cart
                      </button>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => handleViewDetails(productId)}
                          className="inline-flex min-h-[44px] flex-1 items-center justify-center gap-2 rounded-lg border border-white/15 px-4 py-2.5 text-sm text-slate-300 transition-colors hover:border-amber-400/30 hover:text-amber-200 active:scale-[0.98]"
                        >
                          <Eye className="h-4 w-4" aria-hidden="true" />
                          View
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRemoveFavorite(productId)}
                          className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-white/10 text-slate-400 transition-colors hover:border-red-400/30 hover:text-red-400 active:scale-[0.98]"
                          aria-label={`Remove ${displayName} from wishlist`}
                        >
                          <Trash2 className="h-4 w-4" aria-hidden="true" />
                        </button>
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {hasFavorites && (
          <div className="mx-auto mt-10 max-w-3xl border-t border-white/5 pt-8 text-center sm:mt-12 sm:pt-10">
            <button
              type="button"
              onClick={() => navigate('/shop')}
              className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-full border border-amber-400/20 px-6 py-2.5 text-sm text-amber-300 transition-colors hover:border-amber-400/40 hover:bg-amber-400/5 hover:text-amber-200"
            >
              Continue shopping
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        )}
      </div>

      {showAuthModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-slate-950/80 px-4 py-8 backdrop-blur-md"
          onClick={() => setShowAuthModal(false)}
        >
          <div className="relative w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <AuthModalClose onClose={() => setShowAuthModal(false)} />
            {isLogin ? (
              <AuthCard title="Welcome back" subtitle="Sign in to sync your wishlist and checkout">
                <LoginForm
                  onClose={() => setShowAuthModal(false)}
                  switchToSignup={() => setIsLogin(false)}
                />
              </AuthCard>
            ) : (
              <AuthCard title="Create account" subtitle="Join JmoLux to save and shop seamlessly">
                <SignupForm
                  onClose={() => setShowAuthModal(false)}
                  switchToLogin={() => setIsLogin(true)}
                />
              </AuthCard>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default WishlistPage;
